import React from "react";
import {
  determineClimateClassification,
  getClimateColor,
} from "../utils/climateClassification";
import koppenDescriptions from "../data/koppen-descriptions.json";

const ClimateInfoPanel = ({
  temperatureData,
  humidityData,
  selectedLocation,
}) => {
  // If no data is available yet, show a placeholder
  if (
    !temperatureData ||
    !temperatureData.data ||
    !humidityData ||
    !humidityData.data ||
    !selectedLocation
  ) {
    return (
      <div className="climate-info-panel">
        <h2>Climate Classification</h2>
        <p>
          Select a location to view its Köppen-Geiger climate classification.
        </p>
      </div>
    );
  }

  // Determine the climate classification based on temperature and humidity data
  const climateInfo = determineClimateClassification(
    temperatureData.data,
    humidityData.data
  );

  // Get the color for this climate type
  const climateColor = getClimateColor(climateInfo.mainClass);

  // Get detailed description from the Köppen classification JSON
  const getDetailedDescription = () => {
    const { mainClass, subClass } = climateInfo;

    // If we don't have a valid classification, return a default message
    if (mainClass === "Unknown" || !koppenDescriptions[mainClass]) {
      return "This location's climate does not fit standard Köppen-Geiger classifications based on the available data.";
    }

    let description = koppenDescriptions[mainClass].description;

    // Add subtype description if available
    if (subClass && koppenDescriptions[mainClass].subtypes) {
      // Extract the precipitation pattern (first letter of subClass)
      const precipPattern = subClass.charAt(0);

      if (koppenDescriptions[mainClass].subtypes[precipPattern]) {
        description +=
          " " +
          koppenDescriptions[mainClass].subtypes[precipPattern].description;

        // Add temperature modifier description if available (for C and D climates)
        if (
          (mainClass === "C" || mainClass === "D") &&
          subClass.length > 1 &&
          koppenDescriptions[mainClass].subtypes[precipPattern]
            .temperature_modifiers
        ) {
          const tempModifier = subClass.charAt(1);
          if (
            koppenDescriptions[mainClass].subtypes[precipPattern]
              .temperature_modifiers[tempModifier]
          ) {
            description +=
              " This region is " +
              koppenDescriptions[mainClass].subtypes[precipPattern]
                .temperature_modifiers[tempModifier];
          }
        }
      }
    }

    return description;
  };

  const detailedDescription = getDetailedDescription();

  return (
    <div className="climate-info-panel">
      <h2 style={{ borderBottom: "none", paddingBottom: "0" }}>
        Köppen-Geiger Climate Classification
      </h2>

      <div className="climate-info-content">
        <div
          className="climate-code"
          style={{
            backgroundColor: climateColor,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "2.5rem",
            fontWeight: "bold",
            borderRadius: "10px",
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
          }}
        >
          {climateInfo.mainClass}
          {climateInfo.subClass}
        </div>

        <div className="climate-details">
          <h3
            style={{
              fontSize: "1.4rem",
              marginTop: "0",
              color: "#003262",
              borderBottom: "none",
              paddingBottom: "0",
              marginBottom: "15px",
            }}
          >
            {climateInfo.fullName}
          </h3>

          <p
            className="climate-description"
            style={{
              fontSize: "1rem",
              lineHeight: "1.5",
              marginBottom: "20px",
            }}
          >
            {climateInfo.description}
          </p>

          <div className="climate-stats" style={{ gap: "15px" }}>
            <div
              className="stat-item"
              style={{
                padding: "12px",
                borderRadius: "8px",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
              }}
            >
              <span className="stat-label">Annual Avg. Temperature:</span>
              <span className="stat-value">{climateInfo.annualTemp}°C</span>
            </div>
            <div
              className="stat-item"
              style={{
                padding: "12px",
                borderRadius: "8px",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
              }}
            >
              <span className="stat-label">Temperature Range:</span>
              <span className="stat-value">{climateInfo.tempRange}°C</span>
            </div>
            <div
              className="stat-item"
              style={{
                padding: "12px",
                borderRadius: "8px",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
              }}
            >
              <span className="stat-label">Annual Avg. Humidity:</span>
              <span className="stat-value">{climateInfo.annualHumidity}%</span>
            </div>
          </div>
        </div>
      </div>

      <div
        className="climate-info-footer"
        style={{
          marginTop: "25px",
          padding: "15px",
          backgroundColor: "#f5f5f5",
          borderRadius: "8px",
          boxShadow: "inset 0 0 5px rgba(0, 0, 0, 0.05)",
          borderTop: "none",
        }}
      >
        <p style={{ margin: "0", lineHeight: "1.6" }}>
          <strong style={{ color: "#003262" }}>About this climate zone:</strong>{" "}
          {detailedDescription}
        </p>
      </div>
    </div>
  );
};

export default ClimateInfoPanel;
